import {Fragment} from 'react';

import {TokenService as Service} from './TokenService';

import {ToastService} from '../../../AppToast';

import {useToolbar} from '../../../shared/hooks/Toolbar';
import {useDataTable} from '../../../shared/hooks/DataTable';

import {Sign} from '../../../shared/component/Sign';

import {Crup} from './component/crup';
import {ChangePassword} from './component/changePassword';
import {CreateFileCa} from './component/createFileCa';
import {CreateEASYCA} from './component/createEASYCA';
import {CreateNCCA} from './component/createNCCA';
import {CreateICA} from './component/createICA';
import {CreateIntrustCA} from './component/createIntrustCA';
import {useTranslation} from '../../../shared/language';

export const List = () => {

  const {t} = useTranslation();
  const header = t('menu.digitalCertificateRegistration');
  const dataKey = 'id';

  const {render: renderDataTable, refreshLazyData, reloadLazyData, selectedItems} = useDataTable({
    header,
    dataKey,
    columns: [
      {field: 'vender', header: t('common.certificateProvider'), minWidth: 200, matchMode: 'contains'},
      {field: 'subjectName', header: t('common.certificateOwner'), width: 180, matchMode: 'contains'},
      {field: 'serialNumber', header: t('report.certificateSerial'), width: 180, matchMode: 'contains'},
      {field: 'dateFrom', header: t('report.validFrom'), width: 140, dataType: 'date'},
      {field: 'expireDate', header: t('report.expirationDate'), width: 130, dataType: 'date'},
      {field: 'form', header: t('common.tokenRegistrationForm'), width: 150, matchMode: 'contains', filterType: 'dropdown', filterOptions: Service.forms, dataType: 'fromFilterOptions'},
      {field: 'used', header: t('catalog.emailUsage'), width: 90, matchMode: 'equals', filterType: 'boolean', dataType: 'boolean'},
      {field: 'tokenType', header: t('common.certificateType'), width: 130, matchMode: 'contains', filterType: 'dropdown', filterOptions: Service.tokenTypes, dataType: 'fromFilterOptions'},
      {field: 'defaultSign', header: t('common.defaultSign'), width: 110, matchMode: 'equals', filterType: 'boolean', dataType: 'boolean'}
    ],
    indexColumnWidth: 40,
    getList: (params) => {
      return Service.getList(params);
    }
  });

  const crup = Crup({header, dataKey, refreshLazyData});

  const changePassword = ChangePassword({header, dataKey, refreshLazyData});

  const createFileCa = CreateFileCa({header, dataKey, refreshLazyData});

  const createEASYCA = CreateEASYCA({header, dataKey, refreshLazyData});

  const createNCCA = CreateNCCA({header, dataKey, refreshLazyData});

  const createICA = CreateICA({header, dataKey, refreshLazyData});

  const createIntrustCA = CreateIntrustCA({header, dataKey, refreshLazyData});

  const createToken = () => {
    Sign({
      "CommandName": "TokenInfo"
    }).then((data) => {
      if (data && data.data && data.data.token) {
        const {token} = data.data;

        crup.create();

        crup.setValue({
          vender: token.Issuer,
          serialNumber: token.SerialNumber,
          subjectName: token.Subject,
          dateFrom: new Date(token.NotBefore),
          expireDate: new Date(token.NotAfter),
          form: 1,
          tokenType: 0,
          used: true
        });
      } else {
        ToastService.error(t('common.certificateDataInvalid'));
      }
    });
  }

  const {renderToolbar} = useToolbar({
    dataKey,
    reloadLazyData, selectedItems,
    otherActions: [
      {
        label: t('common.addCertificate'), icon: 'pi pi-fw pi-th-large', className: 'iam-toolbar iam-toolbar-success',
        items: [[
          {
            label: '',
            items: [
              {label: t('common.tokenUsb'), command: createToken},
              {label: t('common.tokenFileCa'), command: createFileCa.open},
              {label: t('common.tokenServiceEasyCa'), command: createEASYCA.open},
              {label: t('common.tokenServiceNcca'), command: createNCCA.open},
              {label: t('common.tokenServiceIca'), command: createICA.open},
              {label: t('common.tokenServiceIntrustCa'), command: createIntrustCA.open}
            ]
          }
        ]]
      },
      {
        label: t('common.editCertificate'), icon: 'pi pi-fw pi-pencil', className: 'iam-toolbar', command: () => {
          if (selectedItems && selectedItems['id']) {
            let item: any = selectedItems;
              if (item.dateFrom) {
                item.dateFrom = new Date(item.dateFrom);
              }
              if (item.expireDate) {
                item.expireDate = new Date(item.expireDate);
              }
              crup.update(item);
              reloadLazyData();
          } else {
            ToastService.error(t('common.selectCertificateRequired'));
          }
        }
      },
      {
        label: t('common.deleteCertificate'), icon: 'pi pi-fw pi-trash', className: 'iam-toolbar iam-toolbar-danger', command: () => {
          if (selectedItems && selectedItems['id']) {
            if (!selectedItems['used']) {
              Service.delete(selectedItems['id']).then(() => {
                ToastService.success();
                reloadLazyData();
              });
            } else {
              ToastService.error(t('common.usedCertificateCannotDelete'));
            }
          } else {
            ToastService.error(t('common.selectCertificateRequired'));
          }
        }
      },
      {
        label: t('common.changeCertificatePassword'), icon: 'pi pi-fw pi-unlock', className: 'iam-toolbar iam-toolbar-help', command: () => {
          if (selectedItems && selectedItems['id']) {
            if ([1, 2, 3, 4].includes(selectedItems['tokenType'])) {
              changePassword.open(selectedItems);
            } else {
              ToastService.error(t('common.unsupportedCertificatePasswordChange'));
            }
          } else {
            ToastService.error(t('common.selectCertificateRequired'));
          }
        }
      }
    ]
  });

  return (
    <Fragment>
      {renderToolbar()}
      {renderDataTable()}
      {crup.render()}
      {changePassword.render()}
      {createFileCa.render()}
      {createEASYCA.render()}
      {createNCCA.render()}
      {createICA.render()}
      {createIntrustCA.render()}
    </Fragment>
  )
}
